import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import style from './view-switch-tab.module.css'

const propTypes = {
    children: PropTypes.element,
    className: PropTypes.string,
    hidden: PropTypes.bool,
    iconHeight: PropTypes.number,
    iconWidth: PropTypes.number,
    // Don't remove the 'id' property, because it is needed by the TabBar
    id: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number, PropTypes.func]).isRequired,
    isHighlighted: PropTypes.bool,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onMouseDown: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onTouchStart: PropTypes.func,
    testClassNamePostfix: PropTypes.string
}

const defaultProps = {
    iconHeight: 24,
    iconWidth: 24,
    isHighlighted: false,
    testClassNamePostfix: ''
}

/**
 * Shows a menu tab which consists of an icon counter and
 * uses the {@link CounterElement} to render the counter value.
 *
 * @param {Object} props - The React properties object.
 * @param {Object|Function|string|number} props.id - The current tab id for {@link TabBar}
 * @param {string} [props.className] - The additional tab className string
 * @param {boolean} [props.hidden] - Whether the element should be rendered or not
 * @param {boolean} [props.iconHeight=24] - Height of the icon in pixels
 * @param {boolean} [props.iconWidth=24] - Width of the icon in pixels
 * @param {boolean} [props.isHighlighted=false] - 'true', if the content is highlighted
 * @param {Function} [props.onBlur] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onClick] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onFocus] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onMouseDown] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onMouseEnter] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onMouseLeave] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {Function} [props.onTouchStart] - Important if ViewSwitchTab should be wrapped in a {@link Tooltip}
 * @param {string} [props.testClassNamePostfix=''] - A class name postfix for tests only
 * @returns {ReactElement|null}
 */
function ViewSwitchTab(props) {
    const {
        children,
        className,
        hidden,
        iconHeight,
        iconWidth,
        isHighlighted,
        onBlur,
        onClick,
        onFocus,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart,
        testClassNamePostfix
    } = props

    if (hidden) {
        return null
    }

    const highlightingStyle = { [style['highlighted']]: isHighlighted }
    const testClassName = ['t3sel-icon-tab', testClassNamePostfix].filter(Boolean).join('-')
    const composedClassName = cx(style['view-switch-tab'], highlightingStyle, className, testClassName)
    const viewSwitchTabProps = {
        className: composedClassName,
        onBlur,
        onClick,
        onFocus,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart
    }

    const childrenWithProps = React.Children.map(children, child =>
        React.cloneElement(child, {
            height: iconHeight,
            width: iconWidth
        })
    )

    return <div {...viewSwitchTabProps}>{childrenWithProps}</div>
}

ViewSwitchTab.propTypes = propTypes
ViewSwitchTab.defaultProps = defaultProps

export default ViewSwitchTab
